/**
 * A team's delegation (teamDelegation.ts): what its agents may ask people about and whom they ask first, and who
 * answers for whom. Topics hold an ordered list of answerers (the first is asked first) and how long to wait before
 * an agent may ask the next; cover rules say who may answer for someone, always or while they are away. Everyone
 * named is one of the team's people (its human members and its owner); the problems the platform would refuse are
 * shown in words as they arise, so a save is never a surprise.
 */

import { DELEGATION_WAIT_MAX, DELEGATION_WAIT_MIN, delegationProblems, type DelegationCover, type DelegationTopic, type TeamDelegation } from '@aioe/shared';
import { ChevronDown, ChevronUp, Xmark } from '@gravity-ui/icons';
import { Tooltip } from '@heroui/react';
import type { ReactNode } from 'react';

import { Button, ChoiceSelect, inputClass } from '../ui.tsx';
import { Row } from './MemberEditor.tsx';

const NONE = '__none';
const WHEN_WORDS: Record<DelegationCover['when'], string> = { away: 'While they are away', always: 'Always' };

export interface DelegationPerson {
  id: string;
  name: string;
}

const topicId = (label: string) => `${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'topic'}-${Math.random().toString(36).slice(2, 6)}`;
const minutes = (v: string): number | undefined => (v.trim() && Number.isFinite(Number(v)) ? Math.floor(Number(v)) : undefined);

function IconButton({ label, onPress, isDisabled, children }: { label: string; onPress: () => void; isDisabled?: boolean; children: ReactNode }) {
  return (
    <Tooltip delay={300}>
      <Button size="sm" variant="ghost" isIconOnly aria-label={label} isDisabled={isDisabled} onPress={onPress}>
        {children}
      </Button>
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  );
}

/** An ordered list of people with up, down and remove, and a picker to add one more. */
function PeopleList({ ids, people, ordered, addLabel, onChange }: { ids: string[]; people: DelegationPerson[]; ordered?: boolean; addLabel: string; onChange: (ids: string[]) => void }) {
  const nameOf = (id: string) => people.find((p) => p.id === id)?.name ?? 'Someone not on the team';
  const move = (i: number, by: -1 | 1) => {
    const next = [...ids];
    [next[i], next[i + by]] = [next[i + by]!, next[i]!];
    onChange(next);
  };
  const rest = people.filter((p) => !ids.includes(p.id));
  return (
    <div className="grid w-full gap-1.5">
      {ids.length ? (
        <ol className="m-0 grid list-none gap-1 p-0">
          {ids.map((id, i) => (
            <li key={id} className="flex min-w-0 items-center gap-2 text-sm">
              {ordered ? <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted">{i + 1}.</span> : null}
              <span className={`min-w-0 flex-1 truncate ${people.some((p) => p.id === id) ? '' : 'text-danger'}`}>{nameOf(id)}</span>
              {ordered ? (
                <>
                  <IconButton label={`Ask ${nameOf(id)} earlier`} isDisabled={i === 0} onPress={() => move(i, -1)}>
                    <ChevronUp className="size-4" />
                  </IconButton>
                  <IconButton label={`Ask ${nameOf(id)} later`} isDisabled={i === ids.length - 1} onPress={() => move(i, 1)}>
                    <ChevronDown className="size-4" />
                  </IconButton>
                </>
              ) : null}
              <IconButton label={`Take ${nameOf(id)} off`} onPress={() => onChange(ids.filter((x) => x !== id))}>
                <Xmark className="size-4" />
              </IconButton>
            </li>
          ))}
        </ol>
      ) : null}
      {rest.length ? <ChoiceSelect label={addLabel} className="w-full max-w-64" value={NONE} options={{ [NONE]: `${addLabel}…`, ...Object.fromEntries(rest.map((p) => [p.id, p.name])) }} onChange={(v) => v !== NONE && onChange([...ids, v])} /> : null}
    </div>
  );
}

export function DelegationEditor({ value, people, onChange }: { value: TeamDelegation; people: DelegationPerson[]; onChange: (d: TeamDelegation) => void }) {
  const nameOf = (id: string) => people.find((p) => p.id === id)?.name ?? 'someone not on the team';
  const problems = delegationProblems(value, new Set(people.map((p) => p.id)), nameOf);
  const setTopic = (i: number, patch: Partial<DelegationTopic>) => onChange({ ...value, topics: value.topics.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const setCover = (i: number, patch: Partial<DelegationCover>) => onChange({ ...value, cover: value.cover.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const uncovered = people.filter((p) => !value.cover.some((c) => c.person === p.id));

  if (!people.length) {
    return <p className="m-0 text-sm text-muted">Add a person to the team first: questions go to the team's people, and only they can cover each other.</p>;
  }
  return (
    <div className="grid min-w-0 gap-5">
      <div className="grid min-w-0 gap-2">
        <div className="grid gap-0.5">
          <span className="text-sm font-medium">Topics</span>
          <span className="text-xs text-muted">What the team's agents may ask about, and whom they ask first. After the wait, an agent may decide to ask the next person.</span>
        </div>
        {value.topics.map((t, i) => (
          <div key={t.id} className="grid min-w-0 gap-3 border-t border-separator pt-3">
            <div className="flex min-w-0 items-center gap-2">
              <input className={`${inputClass} min-w-0 flex-1`} aria-label="Topic" value={t.label} placeholder="Billing and refunds" onChange={(e) => setTopic(i, { label: e.target.value })} />
              <IconButton label={`Remove ${t.label || 'this topic'}`} onPress={() => onChange({ ...value, topics: value.topics.filter((_, j) => j !== i) })}>
                <Xmark className="size-4" />
              </IconButton>
            </div>
            <Row label="What it covers" hint={`${(t.describe ?? '').length} of 300 characters.`}>
              <input className={inputClass} aria-label="What it covers" maxLength={300} value={t.describe ?? ''} placeholder="Refund rules, invoices, anything about money owed" onChange={(e) => setTopic(i, { describe: e.target.value || undefined })} />
            </Row>
            <Row label="Who answers, in order">
              <PeopleList ids={t.answerers} people={people} ordered addLabel="Add someone to ask" onChange={(answerers) => setTopic(i, { answerers })} />
            </Row>
            <Row label="Wait before asking the next" hint={`Minutes, ${DELEGATION_WAIT_MIN} to ${DELEGATION_WAIT_MAX.toLocaleString('en-AU')}. Empty: the team's default.`}>
              <input className={`${inputClass} max-w-32 tabular-nums`} aria-label="Wait before asking the next, in minutes" inputMode="numeric" value={t.waitMinutes ?? ''} placeholder="Default" onChange={(e) => setTopic(i, { waitMinutes: minutes(e.target.value) })} />
            </Row>
          </div>
        ))}
        <div>
          <Button size="sm" variant="secondary" onPress={() => onChange({ ...value, topics: [...value.topics, { id: topicId(`topic ${value.topics.length + 1}`), label: '', answerers: [] }] })}>
            Add a topic
          </Button>
        </div>
      </div>

      <div className="grid min-w-0 gap-2">
        <div className="grid gap-0.5">
          <span className="text-sm font-medium">Cover</span>
          <span className="text-xs text-muted">Who may answer a question asked of someone else. The answer says whom it was for.</span>
        </div>
        {value.cover.map((c, i) => (
          <div key={`${c.person}-${i}`} className="grid min-w-0 gap-3 border-t border-separator pt-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <ChoiceSelect label="Whose questions" className="w-full max-w-56" value={c.person} options={Object.fromEntries([...people.filter((p) => p.id === c.person || !value.cover.some((x) => x.person === p.id)), ...(people.some((p) => p.id === c.person) ? [] : [{ id: c.person, name: nameOf(c.person) }])].map((p) => [p.id, p.name]))} onChange={(person) => setCover(i, { person })} />
              <ChoiceSelect label="When" className="w-full max-w-52" value={c.when} options={WHEN_WORDS} onChange={(when) => setCover(i, { when })} />
              <IconButton label={`Remove the cover for ${nameOf(c.person)}`} onPress={() => onChange({ ...value, cover: value.cover.filter((_, j) => j !== i) })}>
                <Xmark className="size-4" />
              </IconButton>
            </div>
            <Row label="Answered for them by">
              <PeopleList ids={c.by} people={people.filter((p) => p.id !== c.person)} addLabel="Add someone who covers" onChange={(by) => setCover(i, { by })} />
            </Row>
          </div>
        ))}
        {uncovered.length ? (
          <div>
            <Button size="sm" variant="secondary" onPress={() => onChange({ ...value, cover: [...value.cover, { person: uncovered[0]!.id, by: [], when: 'away' }] })}>
              Add a cover rule
            </Button>
          </div>
        ) : null}
      </div>

      <Row label="Default wait" hint="Minutes an agent waits for an answer before it may ask the next person, for a topic that sets none.">
        <input className={`${inputClass} max-w-32 tabular-nums`} aria-label="Default wait, in minutes" inputMode="numeric" value={value.defaultWaitMinutes ?? ''} placeholder="None" onChange={(e) => onChange({ ...value, defaultWaitMinutes: minutes(e.target.value) })} />
      </Row>

      {problems.length ? (
        <ul className="m-0 grid list-none gap-1 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="status">
          {problems.map((p) => (
            <li key={`${p.code}-${p.path}`}>{p.message}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
